import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Download, CheckCircle2, FileText, ChevronRight, MessageCircle, AlertTriangle } from "lucide-react";
import useCanonical from "../hooks/useCanonical";
import useJsonLd from "../hooks/useJsonLd";

const MISMATCHES = [
  "HSN code mismatch between GSTR-1 and GSTR-2A",
  "Tax rate mismatch (5% vs 12%, etc.)",
  "GSTIN mismatch on invoice",
  "Invoice value mismatch",
  "Duplicate invoices",
];

const ACTION_ITEMS = [
  "Contact suppliers for unmatched invoices",
  "Correct GSTR-1 if your invoice details are wrong",
  "File amended GSTR-1 if needed (before GSTR-3B due date)",
  "Document all mismatches for your records",
  "Review ITC eligibility (blocked goods/services)",
];

const ITC_REASONS = [
  "Supplier hasn't filed GSTR-1 yet (wait for next month)",
  "Supplier filed but invoice is rejected (contact supplier)",
  "You rejected the invoice (document reason)",
];

const buildWorksheetText = (businessName) => `GST RETURN RECONCILIATION WORKSHEET
Prepared by WINTRUST SOLUTIONS — Coimbatore
For: ${businessName}
https://wintrustsolutions.com

For Month/Quarter: [__________________]

====================================================
STEP 1 — GSTR-1 vs GSTR-2A COMPARISON
====================================================
GSTR-1 (Your Outward Supplies)
  Total invoices filed    : [__________]
  Total value             : Rs. [__________]

GSTR-2A (Inward Supplies Matched)
  Total invoices matched  : [__________]
  Total value matched     : Rs. [__________]

Mismatch analysis:
  - Invoices in GSTR-1 but NOT in supplier's GSTR-1:
    [______] (likely rejected/cancelled)
  - Invoices in GSTR-2A but NOT accepted by you:
    [______] (likely excess ITC)

====================================================
STEP 2 — ITC RECONCILIATION
====================================================
ITC claimed in GSTR-3B  : Rs. [__________]
ITC available in GSTR-2A: Rs. [__________]
Difference              : Rs. [__________]

If difference exists:
  [ ] Supplier hasn't filed GSTR-1 yet (wait for next month)
  [ ] Supplier filed but invoice is rejected (contact supplier)
  [ ] You rejected the invoice (document reason)

====================================================
STEP 3 — COMMON MISMATCHES TO CHECK
====================================================
  [ ] HSN code mismatch between GSTR-1 and GSTR-2A
  [ ] Tax rate mismatch (5% vs 12%, etc.)
  [ ] GSTIN mismatch on invoice
  [ ] Invoice value mismatch
  [ ] Duplicate invoices

====================================================
STEP 4 — ACTION ITEMS
====================================================
  [ ] Contact suppliers for unmatched invoices
  [ ] Correct GSTR-1 if your invoice details are wrong
  [ ] File amended GSTR-1 if needed (before GSTR-3B due date)
  [ ] Document all mismatches for your records
  [ ] Review ITC eligibility (blocked goods/services)

====================================================
DEADLINES
====================================================
Filing deadline      : [________] (as per your category)
Safe filing deadline : [________] (3 days before)

====================================================
NEED EXPERT HELP?
====================================================
WhatsApp : +91 89409 88776
Email    : wintrustsolutions@gmail.com
Service  : https://wintrustsolutions.com/gst-return-filing

DISCLAIMER: This worksheet is a guide only. GST
reconciliation varies by business model. Consult a GST
expert for your specific situation.
`;

const downloadTxt = (filename, text) => {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const FillableRow = ({ label, placeholder = "___" }) => (
  <div className="flex justify-between items-baseline gap-3 py-1.5 border-b border-white/5 last:border-0">
    <span className="text-slate-300 text-sm">{label}</span>
    <span className="text-slate-500 text-xs font-mono tracking-wider">[{placeholder}]</span>
  </div>
);

const GSTReconciliationWorksheetPage = () => {
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useCanonical("/gst-reconciliation-worksheet");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "GST Reconciliation Worksheet — Free Download",
    "description": "Free GST return reconciliation worksheet to match GSTR-1 with GSTR-2A before filing GSTR-3B. Avoid ITC mismatches and notices.",
    "url": "https://wintrustsolutions.com/gst-reconciliation-worksheet",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!businessName.trim()) errs.businessName = "Business name is required";
    if (!phone.trim()) errs.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(phone.trim())) errs.phone = "Enter a valid 10-digit phone";
    if (!email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = "Enter a valid email";

    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    downloadTxt(
      `GST-Reconciliation-Worksheet-${businessName.trim().replace(/\s+/g, "-")}.txt`,
      buildWorksheetText(businessName.trim())
    );

    const waUrl =
      `https://wa.me/918940988776?text=` +
      `Hi%20WinTrust%20Solutions%2C%0A%0A` +
      `I%20downloaded%20the%20GST%20Reconciliation%20Worksheet.%20My%20business%20is%20${encodeURIComponent(businessName.trim())}%20and%20phone%20is%20%2B91${encodeURIComponent(phone.trim())}.%20I%20need%20help%20reconciling%20my%20GST%20returns.`;
    window.open(waUrl, "_blank", "noopener,noreferrer");

    setSubmitted(true);
  };

  return (
    <>
      <Helmet>
        <title>GST Reconciliation Worksheet — Free Download | WINTRUST SOLUTIONS Coimbatore</title>
        <meta name="description" content="Free GST return reconciliation worksheet. Match GSTR-1 with GSTR-2A before filing GSTR-3B. Identify mismatches and avoid notices. Coimbatore." />
      </Helmet>

      {/* Hero */}
      <section className="bg-navy py-12 md:py-16">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">GST Reconciliation Worksheet</span>
          </nav>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-4" data-testid="gst-recon-h1">
            Download GST Return Reconciliation Worksheet
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl">
            Reconcile your GSTR-1 (outward supplies) with GSTR-2A (inward supplies) before filing GSTR-3B. Identify mismatches early and avoid notices.
          </p>
        </div>
      </section>

      {/* Form + Worksheet side-by-side */}
      <section className="bg-navy pb-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Download Form */}
            <div
              className="bg-white rounded-xl p-6 md:p-8 shadow-xl h-fit lg:sticky lg:top-28"
              data-testid="gst-recon-form-container"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-gold/10 text-gold rounded-lg flex items-center justify-center">
                  <Download size={22} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-navy font-heading">Get the Worksheet</h2>
                  <p className="text-slate-500 text-xs">Fillable checklist + expert guidance</p>
                </div>
              </div>

              {submitted ? (
                <div
                  className="bg-green-50 border border-green-200 rounded-lg p-5 flex items-start gap-3"
                  data-testid="gst-recon-success"
                >
                  <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={22} />
                  <div>
                    <p className="text-navy font-semibold mb-1">Worksheet downloaded. Opening WhatsApp...</p>
                    <p className="text-slate-600 text-sm">A GST specialist will respond within 2 hours.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" noValidate data-testid="gst-recon-form">
                  <div>
                    <label className="block text-navy text-sm font-medium mb-2" htmlFor="recon-business-name">
                      Business Name <span className="text-gold">*</span>
                    </label>
                    <input
                      id="recon-business-name"
                      type="text"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy focus:outline-none focus:border-gold"
                      placeholder="Your registered business name"
                      data-testid="recon-business-name-input"
                    />
                    {errors.businessName && <p className="text-red-600 text-xs mt-1" data-testid="recon-business-name-error">{errors.businessName}</p>}
                  </div>

                  <div>
                    <label className="block text-navy text-sm font-medium mb-2" htmlFor="recon-phone">
                      Phone Number <span className="text-gold">*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 bg-slate-100 border border-slate-200 border-r-0 rounded-l-lg text-slate-600 text-sm font-medium">+91</span>
                      <input
                        id="recon-phone"
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-r-lg px-4 py-3 text-navy focus:outline-none focus:border-gold"
                        placeholder="10-digit number"
                        data-testid="recon-phone-input"
                      />
                    </div>
                    {errors.phone && <p className="text-red-600 text-xs mt-1" data-testid="recon-phone-error">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-navy text-sm font-medium mb-2" htmlFor="recon-email">
                      Email <span className="text-gold">*</span>
                    </label>
                    <input
                      id="recon-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy focus:outline-none focus:border-gold"
                      placeholder="you@example.com"
                      data-testid="recon-email-input"
                    />
                    {errors.email && <p className="text-red-600 text-xs mt-1" data-testid="recon-email-error">{errors.email}</p>}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gold hover:bg-gold/90 text-navy font-semibold py-3.5 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-md"
                    data-testid="gst-recon-download-btn"
                  >
                    <Download size={18} />
                    Download Worksheet
                  </button>

                  <p className="text-slate-500 text-xs text-center">
                    You'll get the worksheet as a .txt file and be redirected to WhatsApp.
                  </p>
                </form>
              )}
            </div>

            {/* Worksheet Preview */}
            <div className="text-white space-y-6" data-testid="gst-recon-preview">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-7 backdrop-blur-sm">
                <p className="text-slate-400 text-xs mb-1 uppercase tracking-wider">For Month/Quarter</p>
                <p className="text-white font-mono text-sm">[___________________]</p>
              </div>

              {/* Step 1 */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-7 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gold text-navy font-bold text-sm flex items-center justify-center">1</div>
                  <h3 className="text-lg font-semibold font-heading">GSTR-1 vs GSTR-2A Comparison</h3>
                </div>

                <div className="mb-4">
                  <p className="text-gold text-xs font-semibold mb-2 uppercase tracking-wider">GSTR-1 (Your Outward Supplies)</p>
                  <FillableRow label="Total invoices filed" />
                  <FillableRow label="Total value (₹)" />
                </div>

                <div className="mb-4">
                  <p className="text-gold text-xs font-semibold mb-2 uppercase tracking-wider">GSTR-2A (Inward Supplies Matched)</p>
                  <FillableRow label="Total invoices matched" />
                  <FillableRow label="Total value matched (₹)" />
                </div>

                <div>
                  <p className="text-gold text-xs font-semibold mb-2 uppercase tracking-wider">Mismatch Analysis</p>
                  <FillableRow label="In GSTR-1 but NOT in supplier's GSTR-1" />
                  <FillableRow label="In GSTR-2A but NOT accepted by you" />
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-7 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gold text-navy font-bold text-sm flex items-center justify-center">2</div>
                  <h3 className="text-lg font-semibold font-heading">ITC Reconciliation</h3>
                </div>

                <FillableRow label="ITC claimed in GSTR-3B (₹)" />
                <FillableRow label="ITC available in GSTR-2A (₹)" />
                <FillableRow label="Difference (₹)" />

                <p className="text-gold text-xs font-semibold mt-5 mb-3 uppercase tracking-wider">If difference exists</p>
                <ul className="space-y-2">
                  {ITC_REASONS.map((r) => (
                    <li key={r} className="flex items-start gap-3 text-slate-200 text-sm">
                      <span className="w-4 h-4 flex-shrink-0 mt-0.5 border border-slate-400 rounded-sm inline-block"></span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Step 3 */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-7 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gold text-navy font-bold text-sm flex items-center justify-center">3</div>
                  <h3 className="text-lg font-semibold font-heading">Common Mismatches to Check</h3>
                </div>
                <ul className="space-y-2">
                  {MISMATCHES.map((m) => (
                    <li key={m} className="flex items-start gap-3 text-slate-200 text-sm">
                      <span className="w-4 h-4 flex-shrink-0 mt-0.5 border border-slate-400 rounded-sm inline-block"></span>
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Step 4 */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-7 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gold text-navy font-bold text-sm flex items-center justify-center">4</div>
                  <h3 className="text-lg font-semibold font-heading">Action Items</h3>
                </div>
                <ul className="space-y-2">
                  {ACTION_ITEMS.map((a) => (
                    <li key={a} className="flex items-start gap-3 text-slate-200 text-sm">
                      <span className="w-4 h-4 flex-shrink-0 mt-0.5 border border-slate-400 rounded-sm inline-block"></span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Deadlines */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-7 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="text-gold" size={20} />
                  <h3 className="text-lg font-semibold font-heading">Deadlines</h3>
                </div>
                <FillableRow label="Filing deadline" placeholder="DD-MMM-YYYY" />
                <FillableRow label="Safe filing deadline (3 days before)" placeholder="DD-MMM-YYYY" />
              </div>

              {/* Service link */}
              <div>
                <Link
                  to="/gst-return-filing"
                  className="text-gold font-semibold hover:underline underline-offset-4 inline-flex items-center gap-1"
                  data-testid="link-gst-filing"
                >
                  Need expert help reconciling your GST returns? View our GST Return Filing Services <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          </div>

          {/* Amber CTA */}
          <div className="mt-12 bg-amber-100 border-l-4 border-amber-500 rounded-lg p-6 md:p-8 shadow-md" data-testid="urgent-amber-cta">
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <AlertTriangle className="text-amber-600 flex-shrink-0" size={36} />
              <div className="flex-1">
                <p className="text-navy font-semibold text-base md:text-lg mb-1">
                  Reconciliation prevents costly notices and penalties.
                </p>
                <p className="text-navy/80 text-sm">
                  Most businesses miss ₹50,000+ in ITC due to poor reconciliation. WhatsApp us for a free review of your last 3 months.
                </p>
              </div>
              <a
                href="https://wa.me/918940988776?text=Hi%2C%20I%20need%20help%20reconciling%20my%20GST%20returns%20and%20ITC%20claims"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp flex-shrink-0 whitespace-nowrap"
                data-testid="free-review-btn"
              >
                <MessageCircle size={18} /> Get Free Review — WhatsApp Us
              </a>
            </div>
          </div>

          <p className="mt-8 text-slate-400 text-xs max-w-3xl">
            <strong className="text-slate-200">Disclaimer:</strong> This worksheet is a guide only. GST reconciliation varies by business model. Consult a GST expert for your specific situation.
          </p>
        </div>
      </section>
    </>
  );
};

export default GSTReconciliationWorksheetPage;
